"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _formik = require("formik");
class FormikMiddleware {
  constructor() {
    this._digitFilter = null;
    this._tealium = null;
    this._phoneFormatter = null;
  }

  /**
   * Filters non-digits from input
   *
   * @param {Event} e - onChange Event
   * @param {object} options - Options containing object
   * @param {boolean} options.removeLeadingZeroes - If true, leading zeroes will be removed when typing.
   * @param {boolean} options.currencyInput - If true, then it will remove commas while validating
   * @param {boolean} options.zeroAlways - If true, then it will not allow blank
   * @param {integer} options.allowDecimals - Number of decimals to allow
   * @returns Next instance of the middleware builder function
   */
  digitFilterChainHandler() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this._digitFilter = options;
    return this;
  }

  /**
   * Formats phone number input
   * @param {usaFormat} options - Options containing object
   * @param {boolean} options.usaFormat - If true, then it will format the phone number as (XXX) XXX-XXXX
   * @returns {FormikMiddleware}
   */
  phoneNumberFormatter() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this._phoneFormatter = options;
    return this;
  }

  /**
   * Sends a Tealium `field_input` event when a new input is sent
   *
   * @param {string} category The event_category to be sent with the change event
   * @param {string} label The event_label to be sent with the change event
   * @returns Next instance of the middleware builder function
   */
  tealium(category, label) {
    this._tealium = {
      event_category: category,
      event_label: label
    };
    return this;
  }
  handleChange() {
    var {
      handleChange: formikHandleChange
    } = (0, _formik.useFormikContext)();
    return e => {
      if (this._digitFilter) e = this._handleDigitFilter(e);
      if (this._phoneFormatter) e = this._handlePhoneFormatter(e);
      if (this._tealium) this._sendTealiumEvent(e);
      formikHandleChange(e);
    };
  }
  handleBlur() {
    var removeComma = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var {
      handleBlur: formikHandleBlur
    } = (0, _formik.useFormikContext)();
    return e => {
      if (removeComma) {
        var regex = new RegExp(',', 'g');
        e.target.value = parseInt(e.target.value.replace(regex, ''), 10);
      }
      if (this._digitFilter) e = this._handleDigitFilter(e);
      if (this._tealium) this._sendTealiumEvent(e);
      formikHandleBlur(e);
    };
  }
  _handleDigitFilter(e) {
    // Remove anything not a digit or dot
    e.target.value = e.target.value.replace(/[^\d.]/, '');

    // ** ONLY USE THIS DIGIT FILTER CONFIG WITH ALLOW DECIMALS, DO NOT CHAIN WITH OTHERS **
    if (this._digitFilter.currencyWithDecimal) {
      var numAsString = e.target.value.toString();
      // If empty input, return empty input immediately
      if (numAsString === '') return e;
      // If decimal is entered with no preceeding numbers, do not accept input
      if (numAsString === '.') {
        e.target.value = '';
        return e;
      }
      if (numAsString.indexOf('.') !== -1) {
        // There is a period, split into two and handle each separately
        var splitString = numAsString.split('.');

        // First section
        // Format first section as currency
        var regex = new RegExp(',', 'g');
        splitString[0] = parseInt(splitString[0].replace(regex, ''), 10);
        splitString[0] = new Intl.NumberFormat('en-us').format(splitString[0]);

        // Second section
        // If user tries to enter more than 2 characters after decimal, do not accept input
        if (splitString[1].length > 2) splitString[1] = splitString[1].substring(0, 2);

        // Combine first and second part and assign back to e.target.value
        e.target.value = splitString[0] + '.' + splitString[1];
      } else {
        // It's just the number portion, no decimal. Use currencyFormat
        var _regex = new RegExp(',', 'g');
        e.target.value = parseInt(e.target.value.replace(_regex, ''), 10);
        e.target.value = new Intl.NumberFormat('en-us').format(e.target.value);
      }
    }

    // Remove leading zeroes
    if (this._digitFilter.removeLeadingZeroes && e.target.value.length > 1) e.target.value = e.target.value.replace(/^0+/, '');

    // Format number of decimals
    if (this._digitFilter.allowDecimals) {
      e.target.value = e.target.value.match(new RegExp("[\\d]*\\.{0,1}[\\d]{0,".concat(this._digitFilter.allowDecimals, "}"))) || e.target.value;
    } else e.target.value = e.target.value.replace(/[.]+/, '');

    // If 'allowDecimals' and user types '.' as first character, transform it to '0.'
    if (e.target.value === '.' && this._digitFilter.allowDecimals) {
      e.target.value = '0.';
    }

    // Change blank inputs to zero
    if (e.target.value === '' && this._digitFilter.zeroAlways) {
      e.target.value = 0;
    }

    // Add commas to format currencies properly
    if (this._digitFilter.currencyInput) {
      var _regex2 = new RegExp(',', 'g');
      if (e.target.value !== '') {
        e.target.value = parseInt(e.target.value.replace(_regex2, ''), 10);
        e.target.value = new Intl.NumberFormat('en-us').format(e.target.value);
      }
    }
    return e;
  }

  /* istanbul ignore next */
  _sendTealiumEvent(e) {
    var {
      event_category,
      event_label
    } = this._tealium;
    // eslint-disable-next-line no-undef
    if (event_category && event_label && typeof utag === 'object' && utag.link && e.target.value) {
      // eslint-disable-next-line no-undef
      utag.link({
        event_category: event_category,
        event_action: 'field_input',
        event_label: event_label,
        event_property: e.target.value
      });
    }
  }
  _handlePhoneFormatter(e) {
    var phone = e.target.value.replace(/\D/g, '');
    if (!phone) {
      e.target.value = '';
      return e;
    }
    var phoneLength = phone.length;
    if (this._phoneFormatter.usaFormat) {
      if (phoneLength < 4) {
        e.target.value = "(".concat(phone);
      } else if (phoneLength < 7) {
        e.target.value = "(".concat(phone.slice(0, 3), ") ").concat(phone.slice(3));
      } else {
        e.target.value = "(".concat(phone.slice(0, 3), ") ").concat(phone.slice(3, 6), "-").concat(phone.slice(6, 10));
      }
    }
    return e;
  }
}
var _default = () => new FormikMiddleware();
exports.default = _default;